import React, { useState } from 'react';
import { RefreshControl, ScrollView, TouchableOpacity, View } from 'react-native';
import { Image } from 'expo-image';
import { Ionicons } from '@expo/vector-icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';

import tw from '../../../styles/tailwind';
import Text from '../../../components/Text';
import IcomoonIcon from '../../../components/IcomoonIcon';
import { Illustration } from '../../../components/Illustration';
import { Icon } from '../../../components/Icon';
import IconButton from '../../../components/IconButton';
import api from '../../../utils/api';
import useActiveResidentCommunity from '../../../hooks/useActiveResidentCommunity';
import CommunityUnitHeader from './components/CommunityUnitHeader';

import MyQRCode from './MyQRCode';
import NewGuestPassModal from './GuestPasses/NewGuestPassModal';
import GuestPassDetailModal from './GuestPasses/GuestPassDetailModal';
import EventPassesDetailModal from './GuestPasses/EventPassesDetailModal';
import { EntryPassRow } from './GuestPasses/GuestPassesScreen';
import NewAccessAuthorizationModal from './AccessAuthorizations/NewAccessAuthorizationModal';
import AccessAuthorizationDetailModal from './AccessAuthorizations/AccessAuthorizationDetailModal';
import { AccessAuthorizationRow } from './AccessAuthorizations/AccessAuthorizationsScreen';


function plural(n: number, singular: string, pluralForm: string) {
  return `${n} ${n === 1 ? singular : pluralForm}`;
}

/** "2 activas · 1 pendiente": las vigentes desglosadas por estado. */
function currentAuthsSummary(items: any[]) {
  const count = (status: string) => items.filter(a => (a.display_status ?? a.status) === status).length;
  const parts = [
    count('approved') && plural(count('approved'), 'activa', 'activas'),
    count('pending') && plural(count('pending'), 'pendiente', 'pendientes'),
    count('suspended') && plural(count('suspended'), 'suspendida', 'suspendidas'),
  ].filter(Boolean);

  return parts.length ? parts.join(' · ') : 'Sin autorizaciones activas';
}


// Arriba solo van las vigentes; lo vencido, cancelado o rechazado se consulta en Autorizaciones anteriores
function AccessAuthorizationsBlock({ authorizations, pastCount, onAuthPressed, onNewAuthPressed, onPastPressed }: any) {
  const items: any[] = authorizations ?? [];
  const hasPast = pastCount > 0;
  const hasAuths = items.length > 0 || hasPast;

  return (
    <TouchableOpacity
      onPress={onNewAuthPressed}
      disabled={hasAuths}
      style={tw`bg-[#11554F] rounded-2xl px-4 py-5 gap-y-2`}
    >
      <View style={tw`row`}>
        <View style={tw`w-20 items-center justify-center`}>
          <Illustration name="autorizacion" width={56} height={56} />
        </View>

        <View style={tw`ml-3 flex-1`}>
          <Text style={tw`text-white text-xl`}>Responsivas para proveedores</Text>

          {!hasAuths ? (
            <Text style={tw`text-white`}>Solicita autorización para servicios y proveedores</Text>
          ) : (
            <Text style={tw`text-white`}>{currentAuthsSummary(items)}</Text>
          )}
        </View>
      </View>

      {hasAuths && (
        <>
          <View style={tw`bg-white rounded-xl overflow-hidden mt-3`}>
            {items.map((auth: any) => (
              <AccessAuthorizationRow key={auth.uuid} auth={auth} onPress={() => onAuthPressed?.(auth)} />
            ))}

            {hasPast && (
              <TouchableOpacity style={tw`row justify-between items-center px-4 py-4`} onPress={onPastPressed}>
                <Text style={tw`text-secondary font-500`}>Autorizaciones anteriores</Text>
                <View style={tw`row items-center`}>
                  <Text style={tw`text-gray-400 mr-2`}>{pastCount}</Text>
                  <IcomoonIcon name='chevron-right' color={tw.color('secondary')} />
                </View>
              </TouchableOpacity>
            )}
          </View>

          <IconButton
            title={'Solicitar autorización'}
            style={tw.style(`py-3 px-5 mt-4 self-stretch bg-[#22AA9F]`)}
            onPress={onNewAuthPressed}
          />
        </>
      )}
    </TouchableOpacity>
  );
}


function GuestPassesBlock({ entryPasses, onPassPressed, onNewPassPressed, onBlockPressed }) {
  const hasPasses = entryPasses?.items?.length > 0;

  return (
    <TouchableOpacity
      onPress={!hasPasses ? onNewPassPressed : onBlockPressed}
      style={tw`bg-primary rounded-2xl px-4 py-5 gap-y-2`}
    >
      <View style={tw`row`}>
        <View style={tw`w-20 items-center justify-center`}>
          <Icon name="qr" size={56} />
        </View>

        <View style={tw`ml-3 flex-1`}>
          <Text style={tw`text-[#11554F] text-xl`}>Invitados</Text>

          {!hasPasses ? (
            <Text style={tw`text-white`}>Crea códigos de acceso para tus invitados</Text>
          ) : (
            <View>
              <Text style={tw`text-white`}>
                {entryPasses?.items?.length} llave{entryPasses?.items?.length > 1 ? 's' : ''} activa{entryPasses?.items?.length > 1 ? 's' : ''}
              </Text>
            </View>
          )}
        </View>
      </View>

      {hasPasses && (
        <>
          <View style={tw`bg-white rounded-xl overflow-hidden mt-3`}>
            {entryPasses?.items?.map(pass => (
              <EntryPassRow key={pass.uuid} pass={pass} onPress={() => onPassPressed?.(pass)} />
            ))}
          </View>

          <IconButton
            title={'Nueva llave de acceso'}
            style={tw.style(`py-3 px-5 mt-4 self-stretch bg-[#082B28]`)}
            onPress={onNewPassPressed}
          />
        </>
      )}
    </TouchableOpacity>
  );
}


function tagTypeIcon(type: string): React.ComponentProps<typeof Ionicons>['name'] {
  switch (type) {
    case 'vehicle_tag': return 'car-outline';
    case 'rfid_keychain': return 'key-outline';
    case 'rf_control': return 'radio-outline';
    case 'numeric_code': return 'keypad-outline';
    default: return 'help-outline';
  }
}


function MyTagsBlock({ tags }) {
  const hasTags = tags && tags.items?.length > 0;

  return (
    <View style={tw`bg-primary-50 border border-primary-200 rounded-xl py-4 px-4`}>
      <View style={tw`row justify-between px-4`}>
        <View style={tw`row`}>
          <View style={tw`w-10 items-center`}>
            <Illustration name="tags" width={40} height={40} />
          </View>

          <View style={tw`ml-6 flex-1`}>
            <View style={tw`row`}>
              <Text style={tw`text-secondary font-500 text-lg`}>Mis tags</Text>
            </View>
          </View>
        </View>
      </View>

      {!hasTags && (
        <Text style={tw`text-gray-400 text-center mt-6`}>No tienes tags asignados</Text>
      )}

      {hasTags && (
        <View style={tw`bg-white mt-6 rounded-xl border border-primary-200`}>
          {tags.items.map((tag, i) => (
            <View
              style={tw.style(`px-4 py-3 row justify-between`, i < tags.items.length - 1 && `border-b border-primary-200`)}
              key={tag.uuid}
            >
              <View style={tw`row flex-1`}>
                <Ionicons
                  name={tagTypeIcon(tag.type)}
                  size={18}
                  color={tw.color('secondary')}
                  style={tw`mr-3`}
                />
                <Text style={tw`text-secondary font-500`}>{tag.name}</Text>
              </View>
              <Text style={tw.style(tag.active ? `text-primary` : `text-gray-300`)}>{tag.active ? 'Activo' : 'Inactivo'}</Text>
            </View>
          ))}
        </View>
      )}
    </View>
  );
}


export default function AccessScreen({ navigation }) {
  const insets = useSafeAreaInsets();
  const { communityId } = useActiveResidentCommunity();
  const auth = useSelector((state: any) => state.auth);
  const queryClient = useQueryClient();

  const [showNewGuestPassModal, setShowNewGuestPassModal] = useState(false);
  const [shownGuestPass, setShownGuestPass] = useState(null);
  const [eventGuestPasses, setEventGuestPasses] = useState(null);
  const [showMyQR, setShowMyQR] = useState(false);
  const [showNewAuthModal, setShowNewAuthModal] = useState(false);
  const [shownAuthorization, setShownAuthorization] = useState<any>(null);
  const [authJustCreated, setAuthJustCreated] = useState(false);

  const { isLoading, error, data, refetch } = useQuery({
    queryKey: ['resident.community.dashboard', communityId, auth.user.email],
    queryFn: () => api.get(`/communities/${communityId}/resident/dashboard`).then(response => response.data),
    enabled: !!communityId,
  });

  const accessAuthorizationEnabled = data?.settings?.access_authorization?.enabled;

  const { data: authorizationsResponse } = useQuery({
    queryKey: ['resident.access-authorizations', communityId, 'mine', 'current'],
    queryFn: () =>
      api
        .get(`/communities/${communityId}/access-authorizations`, { params: { mine: 1, period: 'current', per_page: 50 } })
        .then(r => r.data),
    enabled: !!communityId && !!accessAuthorizationEnabled,
  });

  // Solo interesa si hay alguna, para mostrar la celda de Autorizaciones anteriores con su total
  const { data: pastAuthorizationsResponse } = useQuery({
    queryKey: ['resident.access-authorizations', communityId, 'mine', 'past', 'count'],
    queryFn: () =>
      api
        .get(`/communities/${communityId}/access-authorizations`, { params: { mine: 1, period: 'past', per_page: 1 } })
        .then(r => r.data),
    enabled: !!communityId && !!accessAuthorizationEnabled,
  });

  const accessAuthorizations: any[] = authorizationsResponse?.data ?? [];
  const pastAuthorizationsCount: number = pastAuthorizationsResponse?.total ?? 0;

  // Invalida vigentes, anteriores y su conteo; las consultas apagadas no se disparan
  const refetchAuthorizations = () => queryClient.invalidateQueries({ queryKey: ['resident.access-authorizations', communityId] });

  React.useEffect(() => {
    const unsubscribe = navigation.addListener('focus', () => {
      refetch();
      refetchAuthorizations();
    });
    return unsubscribe;
  }, [navigation, communityId]);

  const refetchData = () => {
    refetch();
    refetchAuthorizations();
  };

  const canCreateGuestPasses = data?.settings?.can_create_guest_passes !== false;
  const tagsEnabled = data?.settings?.tags_enabled;
  const showMyQrEnabled = data?.settings?.show_my_qr;
  const canScanQrEnabled = data?.settings?.can_scan_qr;
  const hasAnyAccessAction = showMyQrEnabled || canScanQrEnabled;

  return (
    <ScrollView
      contentContainerStyle={tw`pb-24`}
      style={tw`bg-gray-100`}
      refreshControl={<RefreshControl onRefresh={refetchData} refreshing={isLoading} />}
    >
      {/* Header con QR */}
      <View style={tw.style(`bg-secondary rounded-b-2xl px-4 pb-4`, { paddingTop: insets.top + 20 })}>
        <Text style={tw`text-white text-2xl mb-4 text-center`}>{hasAnyAccessAction ? 'Mi acceso' : 'Accesos'}</Text>

        <CommunityUnitHeader community={data?.community} unit={data?.unit} />

        <View style={tw`items-center mt-3`}>

        {showMyQR && showMyQrEnabled && (
          <>
            <Text style={tw`text-white/70 mb-8 text-center`}>Muestra este código en la entrada</Text>
            <View style={tw`my-8`}>
              <MyQRCode size={260} />
            </View>
          </>
        )}

        {hasAnyAccessAction && (
          <View style={tw`row gap-x-3 w-full`}>
            {showMyQrEnabled && (
              <TouchableOpacity
                style={tw`flex-1 row items-center justify-center bg-white/15 px-5 py-3 rounded-xl`}
                onPress={() => setShowMyQR(!showMyQR)}
              >
                <IcomoonIcon name='qr-dd' color={'white'} size={22} />
                <Text style={tw`text-white ml-3`}>{showMyQR ? 'Ocultar mi QR' : 'Mostrar mi QR'}</Text>
              </TouchableOpacity>
            )}

            {canScanQrEnabled && (
              <TouchableOpacity
                style={tw`flex-1 row items-center justify-center bg-white/15 px-5 py-3 rounded-xl`}
                onPress={() => navigation.navigate('Resident.Scanner')}
              >
                <IcomoonIcon name='qr-dd' color={'white'} size={22} />
                <Text style={tw`text-white ml-3`}>Escanear un QR</Text>
              </TouchableOpacity>
            )}
          </View>
        )}
        </View>
      </View>


      <View style={tw`px-4 pt-4`}>
        {canCreateGuestPasses && (
          <View style={tw`mb-4`}>
            <GuestPassesBlock
              entryPasses={data?.entry_passes}
              onPassPressed={(pass) => setShownGuestPass(pass)}
              onNewPassPressed={() => setShowNewGuestPassModal(true)}
              onBlockPressed={() => navigation.navigate('Resident.GuestPasses.Index', { communityId, unitFeeId: data.unit.uuid })}
            />
          </View>
        )}

        {accessAuthorizationEnabled && (
          <View style={tw`mb-4`}>
            <AccessAuthorizationsBlock
              authorizations={accessAuthorizations}
              pastCount={pastAuthorizationsCount}
              onAuthPressed={(auth: any) => { setAuthJustCreated(false); setShownAuthorization(auth); }}
              onNewAuthPressed={() => setShowNewAuthModal(true)}
              onPastPressed={() => navigation.navigate('Resident.AccessAuthorizations.Index', { communityId })}
            />
          </View>
        )}

        {tagsEnabled && (
          <View style={tw`mb-6`}>
            <MyTagsBlock tags={data?.access_tags} />
          </View>
        )}
      </View>


      <NewGuestPassModal
        visible={showNewGuestPassModal}
        unitId={data?.unit?.uuid}
        settings={data?.settings?.guest_passes}
        communityId={communityId}
        onRequestClose={() => setShowNewGuestPassModal(false)}
        onGuestPassCreated={(guestPass) => {
          refetch();
          setShowNewGuestPassModal(false);

          if (guestPass.constructor.name == "Array") {
            setEventGuestPasses(guestPass);
          } else {
            setShownGuestPass(guestPass);
          }
        }}
      />

      <GuestPassDetailModal
        visible={!!shownGuestPass}
        guestPass={shownGuestPass}
        community={communityId}
        unitId={data?.unit?.uuid}
        onRequestClose={() => setShownGuestPass(null)}
        onDeleted={() => {
          refetch();
          setShownGuestPass(null);
        }}
      />

      {eventGuestPasses && (
        <EventPassesDetailModal
          visible={true}
          guestPasses={eventGuestPasses}
          onRequestClose={() => setEventGuestPasses(null)}
        />
      )}

      {accessAuthorizationEnabled && (
        <>
          <NewAccessAuthorizationModal
            visible={showNewAuthModal}
            communityId={communityId}
            unitId={data?.unit?.uuid}
            settings={data?.settings?.access_authorization}
            onRequestClose={() => setShowNewAuthModal(false)}
            onCreated={(auth) => {
              setShowNewAuthModal(false);
              refetchAuthorizations();
              setAuthJustCreated(true);
              setShownAuthorization(auth);
            }}
          />

          <AccessAuthorizationDetailModal
            visible={!!shownAuthorization}
            authorization={shownAuthorization}
            communityId={communityId}
            justCreated={authJustCreated}
            onRequestClose={() => { setShownAuthorization(null); setAuthJustCreated(false); }}
            onChanged={refetchAuthorizations}
          />
        </>
      )}
    </ScrollView>
  );
}
